import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import type { Organization } from './organizationService';

export type EvidenceType = 'FILE' | 'SYSTEM_RECORD' | 'DATA_ONLY' | 'ATTESTATION';
export type EvidenceSource = 'UPLOAD' | 'CORE_SYSTEM' | 'DOC_SYSTEM' | 'FLOOD_VENDOR' | 'APPRAISAL_PLATFORM' | 'MANUAL' | 'OTHER';
export type DeliveryMethod = 'MAIL' | 'EMAIL' | 'ESIGN_PORTAL' | 'IN_PERSON' | 'OTHER';

export interface ChecklistEvidence {
  id?: string;
  // Composite key: checklistCompletionId + itemText identifies the checklist item result
  checklistCompletionId: string; // Reference to the checklist completion
  itemText: string; // The checklist item text this evidence is for (matches key in ChecklistCompletion.items map)
  
  // Evidence classification
  evidenceType: EvidenceType;
  evidenceName: string; // Name/label for this evidence (from checklist definition)
  source: EvidenceSource;
  
  // Field validation status
  fieldStatus?: {
    totalFields: number;
    requiredFields: number;
    populatedFields: number;
    validatedFields: number;
    missingFields: string[]; // Array of field keys that are missing
    invalidFields: string[]; // Array of field keys that failed validation
  };
  
  // Validation summary
  validationStatus?: 'COMPLETE' | 'INCOMPLETE' | 'INVALID';
  validationSummary?: string; // Human-readable summary of validation issues
  
  // Field values (for display)
  fieldValues?: Record<string, {
    value: any;
    populated: boolean;
    validated: boolean;
    source?: string; // File name or source
  }>;
  
  // Evidence details (optional, for FILE type evidence)
  evidenceDate?: any; // Date of the event/document (nullable but recommended)
  deliveryMethod?: DeliveryMethod;
  recordId?: string; // e.g., vendor cert ID, tracking #, doc package ID
  recordUrl?: string; // Internal reference URL
  documentName?: string;
  notes?: string;
  
  // File reference (required when evidenceType = FILE)
  fileId?: string; // Points to file storage record
  fileUrl?: string; // Direct file URL (for convenience)
  
  // Metadata
  organizationId: string;
  addedByUserId: string;
  addedAt?: any;
  createdAt?: any;
  updatedAt?: any;
}

// Helper function to generate composite key for checklist item result
// This is a deterministic way to identify a specific checklist item within a completion
export function getChecklistItemResultId(checklistCompletionId: string, itemText: string): string {
  return `${checklistCompletionId}:${itemText}`;
}

// Helper function to remove undefined values from an object (Firestore doesn't accept undefined)
const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        cleaned[key] = removeUndefined(obj[key]);
      }
    }
    return cleaned;
  }
  return obj;
};

// Validation function
async function validateEvidence(
  evidence: Omit<ChecklistEvidence, 'id' | 'addedAt' | 'createdAt' | 'updatedAt'>,
  organization?: Organization
): Promise<{ valid: boolean; error?: string }> {
  // 1. If evidenceType = FILE, fileId is required
  if (evidence.evidenceType === 'FILE' && !evidence.fileId) {
    return { valid: false, error: 'fileId is required when evidenceType is FILE' };
  }

  // 2. evidenceName is required
  if (!evidence.evidenceName || evidence.evidenceName.trim() === '') {
    return { valid: false, error: 'evidenceName is required' };
  }

  // Category validation removed - evidence names are dynamic from checklist definitions
  // No need to validate against a fixed list

  return { valid: true };
}

export const checklistEvidenceService = {
  // Add a new evidence record
  addEvidence: async (
    evidenceData: Omit<ChecklistEvidence, 'id' | 'addedAt' | 'createdAt' | 'updatedAt'>,
    organization?: Organization,
    skipCategoryValidation?: boolean // Deprecated - kept for backward compatibility, no longer used
  ): Promise<{ success: boolean; id?: string; error?: string }> => {
    try {
      // Validate evidence (category validation removed)
      const validation = await validateEvidence(evidenceData, organization);
      if (!validation.valid) {
        return { success: false, error: validation.error };
      } else {
        // Still validate basic requirements (fileId for FILE type, etc.)
        if (evidenceData.evidenceType === 'FILE' && !evidenceData.fileId) {
          return { success: false, error: 'fileId is required when evidenceType is FILE' };
        }
      }

      const evidenceRef = collection(db, 'checklist_evidence');
      const cleanedData = removeUndefined({
        ...evidenceData,
        addedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const docRef = await addDoc(evidenceRef, cleanedData);
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding checklist evidence:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all evidence for a checklist item result
  getEvidenceByItemResult: async (
    checklistCompletionId: string,
    itemText: string
  ): Promise<{ success: boolean; data?: ChecklistEvidence[]; error?: string }> => {
    try {
      const evidenceRef = collection(db, 'checklist_evidence');
      const q = query(
        evidenceRef,
        where('checklistCompletionId', '==', checklistCompletionId),
        where('itemText', '==', itemText),
        orderBy('addedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const evidence: ChecklistEvidence[] = [];
      
      querySnapshot.forEach((doc) => {
        evidence.push({ id: doc.id, ...doc.data() } as ChecklistEvidence);
      });

      return { success: true, data: evidence };
    } catch (error: any) {
      console.error('Error getting checklist evidence:', error);
      // If it's an index error, provide helpful message
      if (error.code === 'failed-precondition' && error.message?.includes('index')) {
        console.error('Firestore index required. Please create the composite index at:', error.message);
      }
      return { success: false, error: error.message };
    }
  },

  // Get all evidence for a checklist completion
  getEvidenceByCompletion: async (
    checklistCompletionId: string
  ): Promise<{ success: boolean; data?: ChecklistEvidence[]; error?: string }> => {
    try {
      const evidenceRef = collection(db, 'checklist_evidence');
      const q = query(
        evidenceRef,
        where('checklistCompletionId', '==', checklistCompletionId),
        orderBy('itemText', 'asc'),
        orderBy('addedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const evidence: ChecklistEvidence[] = [];
      
      querySnapshot.forEach((doc) => {
        evidence.push({ id: doc.id, ...doc.data() } as ChecklistEvidence);
      });

      return { success: true, data: evidence };
    } catch (error: any) {
      console.error('Error getting checklist evidence:', error);
      return { success: false, error: error.message };
    }
  },

  // Get evidence by fileId (to find which checklist items use this file)
  getEvidenceByFileId: async (
    fileId: string,
    organizationId: string
  ): Promise<{ success: boolean; data?: ChecklistEvidence[]; error?: string }> => {
    try {
      const evidenceRef = collection(db, 'checklist_evidence');
      const q = query(
        evidenceRef,
        where('fileId', '==', fileId),
        where('organizationId', '==', organizationId),
        orderBy('addedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const evidence: ChecklistEvidence[] = [];
      
      querySnapshot.forEach((doc) => {
        evidence.push({ id: doc.id, ...doc.data() } as ChecklistEvidence);
      });

      return { success: true, data: evidence };
    } catch (error: any) {
      console.error('Error getting evidence by fileId:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a single evidence record by ID
  getEvidence: async (evidenceId: string): Promise<{ success: boolean; data?: ChecklistEvidence; error?: string }> => {
    try {
      const evidenceRef = doc(db, 'checklist_evidence', evidenceId);
      const docSnap = await getDoc(evidenceRef);
      
      if (docSnap.exists()) {
        return { success: true, data: { id: docSnap.id, ...docSnap.data() } as ChecklistEvidence };
      } else {
        return { success: false, error: 'Evidence not found' };
      }
    } catch (error: any) {
      console.error('Error getting checklist evidence:', error);
      return { success: false, error: error.message };
    }
  },

  // Update an evidence record
  updateEvidence: async (
    evidenceId: string,
    evidenceData: Partial<ChecklistEvidence>,
    organization?: Organization
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // Get current evidence to merge with updates
      const currentResult = await checklistEvidenceService.getEvidence(evidenceId);
      if (!currentResult.success || !currentResult.data) {
        return { success: false, error: 'Evidence not found' };
      }

      const currentEvidence = currentResult.data;
      const updatedEvidence = { ...currentEvidence, ...evidenceData };

      // Validate updated evidence
      const validation = await validateEvidence(
        updatedEvidence as Omit<ChecklistEvidence, 'id' | 'addedAt' | 'createdAt' | 'updatedAt'>,
        organization
      );
      if (!validation.valid) {
        return { success: false, error: validation.error };
      }

      const evidenceRef = doc(db, 'checklist_evidence', evidenceId);
      const cleanedData = removeUndefined({
        ...evidenceData,
        updatedAt: serverTimestamp(),
      });

      await updateDoc(evidenceRef, cleanedData);
      return { success: true };
    } catch (error: any) {
      console.error('Error updating checklist evidence:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete an evidence record
  deleteEvidence: async (evidenceId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const evidenceRef = doc(db, 'checklist_evidence', evidenceId);
      await deleteDoc(evidenceRef);
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting checklist evidence:', error);
      return { success: false, error: error.message };
    }
  },

  // Get evidence by organization (for admin/reporting)
  getEvidenceByOrganization: async (
    organizationId: string,
    limit?: number
  ): Promise<{ success: boolean; data?: ChecklistEvidence[]; error?: string }> => {
    try {
      const evidenceRef = collection(db, 'checklist_evidence');
      let q = query(
        evidenceRef,
        where('organizationId', '==', organizationId),
        orderBy('addedAt', 'desc')
      );

      if (limit) {
        // Note: Firestore doesn't support limit in query builder directly for this use case
        // You'd need to use limit() from firebase/firestore if needed
      }

      const querySnapshot = await getDocs(q);
      const evidence: ChecklistEvidence[] = [];
      
      querySnapshot.forEach((doc) => {
        evidence.push({ id: doc.id, ...doc.data() } as ChecklistEvidence);
      });

      // Apply limit in memory if specified
      const limitedEvidence = limit ? evidence.slice(0, limit) : evidence;

      return { success: true, data: limitedEvidence };
    } catch (error: any) {
      console.error('Error getting evidence by organization:', error);
      return { success: false, error: error.message };
    }
  },
};

